/**
 * `null` -> `''` pe campurile unui obiect de adresa venit din backend.
 *
 * 🔴 De ce exista (issue #15): backendul trimite `null` pentru orice coloana de adresa
 * necompletata — cazul OBISNUIT, fiindca sunt optionale. Dar `addressSchema` foloseste
 * `z.string().optional()`, care accepta `undefined` si **respinge `null`**, cu mesajul brut al lui
 * Zod: „Expected string, received null". Formularul se bloca pe CLIENT, cu sase campuri etichetate
 * „Optional" marcate invalid. **1.194 din 1.225 de scoli (97,5%)** nu-si puteau salva detaliile.
 *
 * ⚠️ De ce AICI si nu in schema: un `z.preprocess(...)` sau `.transform(...)` pe campuri produce
 * `ZodEffects`, al carui tip de intrare devine `unknown` — ceea ce propaga `unknown` prin
 * `satisfies z.ZodType<Partial<SchoolDto>, …>` si prin props-urile de adresa din formularele de
 * comanda. Masurat: **30 de erori TS noi** fata de baseline cu `preprocess`, **32** cu
 * `.nullish().transform()`. Clichetul de typecheck ruleaza si in `deploy-frontend.yml`, deci
 * varianta „centrala" ar fi oprit deploy-ul.
 *
 * Sanitizarea la sursa are blast radius zero pe tipuri, si urmeaza tiparul deja folosit in cele
 * patru locuri acoperite din inventarul issue-ului #16.
 *
 * 🔴 REGULA, pentru oricine adauga un consumator NOU al lui `addressSchema`:
 * **schema NU accepta `null`, si asta e prin design de tipuri, nu o scapare.** Orice incercare de
 * a o face sa-l accepte (`preprocess`, `.transform`, `.nullish`) produce `ZodEffects` si propaga
 * `unknown` prin `satisfies z.ZodType<Partial<SchoolDto>, …>` si prin props-urile de adresa —
 * masurat: 30 de erori TS cu `preprocess`, 32 cu `.transform`. Iar `satisfies` merita pastrat: e
 * garda care tine schema aliniata cu DTO-ul.
 *
 * Deci: **datele venite din backend trec prin `sanitizeAddress` la INCARCARE**, inainte de
 * `form.reset(...)` / `defaultValues`. Azi sunt sase puncte de consum acoperite asa (patru dinainte,
 * doua adaugate odata cu #15). Un al saptelea care sare peste pas reintroduce exact acelasi blocaj.
 *
 * NU converteste `undefined` — acela e deja acceptat de `z.string().optional()`, iar transformarea
 * lui in `''` ar schimba semnificatia campului din „neatins" in „golit explicit".
 */
export function sanitizeAddress<T extends Record<string, unknown>>(adresa: T): T;
export function sanitizeAddress<T extends Record<string, unknown>>(
    adresa: T | null | undefined,
): T | undefined;
export function sanitizeAddress<T extends Record<string, unknown>>(
    adresa: T | null | undefined,
): T | undefined {
    if (adresa === null || adresa === undefined) return undefined;
    const rezultat: Record<string, unknown> = {};
    for (const [cheie, valoare] of Object.entries(adresa)) {
        rezultat[cheie] = valoare === null ? '' : valoare;
    }
    return rezultat as T;
}
